import { ISavedViewSettings } from '~/client/src/shared/enums/ProjectSpecificUserProps'
import UserProject from '~/client/src/shared/models/UserProject'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'

export interface ViewItem {
  id: string
  name: string
  isPublic: boolean
  filtersByFilterType?: Array<{
    type?: string | null
    values?: Array<string | null> | null
  } | null> | null
  groupBy?: string | null
  userId?: string
  isHidden: boolean
  order: number
}

export interface SavedView {
  id: string
  name: string
  isPublic: boolean
  filtersByFilterType?: Array<{
    type?: string | null
    values?: Array<string | null> | null
  } | null> | null
  groupBy?: string | null
  userId?: string
}

export const getFilterIdField = (
  filterType: 'resources' | 'workflows' | 'uploadLog' | 'notifications',
): keyof ISavedViewSettings => {
  switch (filterType) {
    case 'resources':
      return 'resourcesCustomFilterId'
    case 'workflows':
      return 'workflowCustomFilterId'
    case 'uploadLog':
      return 'uploadLogCustomFilterId'
    case 'notifications':
      return 'notificationCustomFilterId'
  }
}

export const transformViewsToItems = (
  views: SavedView[],
  savedViewsSettings: ISavedViewSettings[] | undefined,
  filterType: 'resources' | 'workflows' | 'uploadLog' | 'notifications',
): ViewItem[] => {
  const filterIdField = getFilterIdField(filterType)
  const settings = savedViewsSettings || []

  const relevantSettings = settings.filter(
    setting => setting[filterIdField] != null,
  )

  const settingsMap = new Map(
    relevantSettings.map(setting => [
      setting[filterIdField] as string,
      setting,
    ]),
  )

  const existingViewIds = new Set(views.map(v => v.id))
  const validSettings = relevantSettings
    .filter(setting => existingViewIds.has(setting[filterIdField] as string))
    .map((setting, index) => ({
      id: setting[filterIdField] as string,
      isHidden: setting.isHidden || false,
      order: index,
    }))

  const viewsWithSettings = views.map(view => {
    const setting = settingsMap.get(view.id)
    const settingIndex = validSettings.findIndex(s => s.id === view.id)

    return {
      ...view,
      isHidden: setting?.isHidden || false,
      order:
        settingIndex !== -1
          ? settingIndex
          : validSettings.length + views.indexOf(view),
    }
  })

  return viewsWithSettings.sort((a, b) => a.order - b.order)
}

export const updateSavedViewSettings = (
  userProjectsStore: UserProjectsStore | undefined,
  userActiveProjectSettings: UserProject | undefined,
  filterType: 'resources' | 'workflows' | 'uploadLog' | 'notifications',
  updater: (settings: ISavedViewSettings[]) => ISavedViewSettings[],
) => {
  if (!userProjectsStore || !userActiveProjectSettings) {
    return
  }

  const existingSettings = userActiveProjectSettings.savedViewsSettings || []
  const newSettings = updater(existingSettings)

  const userProjectDto = userActiveProjectSettings.toDto()
  userProjectDto.savedViewsSettings = newSettings

  userProjectsStore.save([userProjectDto])
}
